import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

import { CloseButton, Dialog, DialogActions, DialogContent, DialogTitle, Loading, SubmitButton } from 'components';
import { useBuilding } from 'pages/buildings/buildings.hook';
import { useDeleteBuildingMutation } from 'store/apis';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { ROUTES } from 'consts';

interface BuildingDeleteDialogProps {
  visible?: boolean;
  setVisible?: (value: boolean) => void;
  title?: string;
  submitButtonLabel?: string;
  buildingId: number;
}

export const BuildingDeleteDialog: React.FC<BuildingDeleteDialogProps> = ({
  visible = false,
  setVisible = () => {},
  title = 'Confirm delete building',
  submitButtonLabel = 'Delete',
  buildingId,
}: BuildingDeleteDialogProps) => {
  const [loadingBuilding] = useBuilding({ buildingId });
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [deleteBuilding, { isLoading: isDeleting, isSuccess, isError }] = useDeleteBuildingMutation();
  const navigate = useNavigate();

  const onClose = (event: any, reason: string) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
      setVisible(false);
      document.body.removeAttribute('style');
    }
  };

  const onDelete = () => {
    if (orgSlug && buildingId) {
      deleteBuilding({
        orgSlug,
        id: buildingId,
      });
    }
  };

  useEffect(() => {
    if (!isDeleting && isSuccess && !isError) {
      onClose(null, 'anyway');
      navigate(`/${orgSlug}/${ROUTES.BUILDINGS}`);
    }
  }, [isDeleting, isSuccess, isError]);

  return (
    <Dialog open={visible} onClose={onClose} fullScreen={false}>
      <DialogTitle>
        <Typography variant="h5" component="span">
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loadingBuilding ? (
          <Loading text={'Loading Building...'} height={240} />
        ) : (
          <Box
            sx={{
              paddingBottom: 2,
            }}
          >
            <Typography variant="body2" color="textSecondary">
              Do you really want to delete this building?
            </Typography>
            <Typography variant="body2" color="textSecondary">
              This process cannot be undone.
            </Typography>
          </Box>
        )}
      </DialogContent>

      {!loadingBuilding && (
        <DialogActions>
          <SubmitButton loading={isDeleting} label={submitButtonLabel} onClick={onDelete} />
        </DialogActions>
      )}

      <CloseButton onClick={() => onClose(null, 'anyway')} />
    </Dialog>
  );
};
